/**
 * Canonical SOP authoring document (schema_version 1).
 * Shared by React editor and server draft/publish validation.
 */

import {
  isMultipleChoiceStep,
  parseMultipleChoiceConfig,
} from './multiple-choice-step.domain.js';

export const SOP_AUTHORING_DOCUMENT_SCHEMA_VERSION = 1 as const;

export type SopAuthoringScopeType = 'GLOBAL' | 'DEALER' | 'SITE';

export type SopAuthoringSopMeta = {
  name: string;
  description: string | null;
  scope_type: SopAuthoringScopeType;
  scope_id: number | null;
  is_inheritable: boolean;
  is_hidden_from_descendants: boolean;
};

export type SopAuthoringStep = {
  draft_step_id: string;
  published_step_id: number | null;
  position: number;
  title: string;
  archetype: string | null;
  variant: string | null;
  prompt_text: string | null;
  camera_ref: string | null;
  url: string | null;
  config: unknown | null;
  contact_reference_type: string | null;
  contact_reference_value: string | number | null;
  contact_reference_scope: string | null;
  url_reference_type: string | null;
  url_reference_value: string | null;
  choice_positive_label: string | null;
  choice_negative_label: string | null;
  positive_response_format: string | null;
  negative_response_format: string | null;
  include_negative_in_report: boolean;
  include_in_report: boolean;
  show_in_narrative_strip: boolean;
  report_action_text: string | null;
  is_active: boolean;
  /** draft_step_id targets only */
  next_step: string | null;
  yes_next_step: string | null;
  no_next_step: string | null;
};

export type SopAuthoringDocument = {
  schema_version: typeof SOP_AUTHORING_DOCUMENT_SCHEMA_VERSION;
  sop: SopAuthoringSopMeta;
  steps: SopAuthoringStep[];
};

export type PublishedSopForAuthoring = {
  name?: string | null;
  description?: string | null;
  owner_type?: string | null;
  scope_type?: string | null;
  owner_id?: number | null;
  scope_id?: number | null;
  is_inheritable?: boolean | number | null;
  is_hidden_from_descendants?: boolean | number | null;
};

export type PublishedStepForAuthoring = {
  id: number;
  step_order?: number | null;
  title?: string | null;
  archetype?: string | null;
  variant?: string | null;
  prompt_text?: string | null;
  camera_ref?: string | null;
  url?: string | null;
  config?: unknown;
  contact_reference_type?: string | null;
  contact_reference_value?: string | number | null;
  contact_reference_scope?: string | null;
  url_reference_type?: string | null;
  url_reference_value?: string | null;
  choice_positive_label?: string | null;
  choice_negative_label?: string | null;
  positive_response_format?: string | null;
  negative_response_format?: string | null;
  include_negative_in_report?: boolean | number | null;
  include_in_report?: boolean | number | null;
  show_in_narrative_strip?: boolean | number | null;
  report_action_text?: string | null;
  is_active?: boolean | number | null;
  next_step?: string | number | null;
  yes_next_step?: string | number | null;
  no_next_step?: string | number | null;
};

function parseBooleanFlag(value: unknown, fallback = false): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (value == null) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return fallback;
  if (normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on') return true;
  if (normalized === '0' || normalized === 'false' || normalized === 'no' || normalized === 'off') return false;
  return fallback;
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text.length ? text : null;
}

export function isPublishedDraftStepId(draftStepId: string): boolean {
  return /^step:\d+$/.test(String(draftStepId || '').trim());
}

export function isTemporaryDraftStepId(draftStepId: string): boolean {
  return /^tmp:[0-9a-f-]{36}$/i.test(String(draftStepId || '').trim());
}

export function publishedStepDraftId(publishedStepId: number): string {
  return `step:${Number(publishedStepId)}`;
}

export function newTemporaryDraftStepId(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID !== 'function') {
    throw new Error('crypto.randomUUID is unavailable');
  }
  return `tmp:${webCrypto.randomUUID()}`;
}

function linkPublishedIdToDraftId(
  value: string | number | null | undefined,
  publishedIdSet: Set<number>,
): string | null {
  if (value == null || value === '') return null;
  const asDraft = String(value).trim();
  if (asDraft.startsWith('step:') || asDraft.startsWith('tmp:')) return asDraft;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  if (!publishedIdSet.has(numeric)) return null;
  return publishedStepDraftId(numeric);
}

function normalizeLinkTarget(
  value: unknown,
  knownDraftIds: Set<string>,
): string | null {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  if (!text) return null;
  if (knownDraftIds.has(text)) return text;
  if (/^\d+$/.test(text)) {
    const asStep = publishedStepDraftId(Number(text));
    if (knownDraftIds.has(asStep)) return asStep;
  }
  throw new Error(`Unknown link target "${text}"`);
}

function readOptionNextDraftId(raw: unknown, knownDraftIds: Set<string>): string | null {
  if (raw == null || raw === '') return null;
  const asText = String(raw).trim();
  if (!asText) return null;
  if (asText.startsWith('step:') || asText.startsWith('tmp:')) {
    return normalizeLinkTarget(asText, knownDraftIds);
  }
  if (/^\d+$/.test(asText)) {
    return normalizeLinkTarget(publishedStepDraftId(Number(asText)), knownDraftIds);
  }
  return normalizeLinkTarget(asText, knownDraftIds);
}

/**
 * Normalize MULTI_CHOICE options for the authoring document.
 * Option destinations stay as draft_step_id strings (not DB ints).
 */
function normalizeMultiChoiceConfigLinks(
  config: unknown,
  knownDraftIds: Set<string>,
): { options: Array<{ id: string; label: string; report_output: string; next_step_id: string | null }> } {
  let source = config;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      source = { options: [] };
    }
  }
  const obj = source != null && typeof source === 'object' ? (source as Record<string, unknown>) : {};
  const rawOptions = Array.isArray(obj.options) ? obj.options : [];
  const usedIds = new Set<string>();
  const options: Array<{ id: string; label: string; report_output: string; next_step_id: string | null }> = [];

  for (let index = 0; index < rawOptions.length; index += 1) {
    const entry = rawOptions[index];
    if (entry == null || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const label = String(row.label ?? row.value ?? '').trim();
    let id = String(row.id ?? '').trim();
    if (!id || usedIds.has(id)) {
      const slug = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 32) || `option_${index + 1}`;
      let candidate = id && !usedIds.has(id) ? id : `option_${slug}`;
      let suffix = 2;
      while (usedIds.has(candidate)) {
        candidate = `option_${slug}_${suffix}`;
        suffix += 1;
      }
      id = candidate;
    }
    usedIds.add(id);
    const reportRaw = row.report_output ?? row.reportText ?? row.report_output_text ?? row.reportOutput;
    options.push({
      id,
      label,
      report_output: reportRaw == null ? '' : String(reportRaw).trim(),
      next_step_id: readOptionNextDraftId(row.next_step_id ?? row.nextStepId ?? row.next_step, knownDraftIds),
    });
  }
  return { options };
}

function coerceScopeType(value: unknown): SopAuthoringScopeType {
  const text = String(value || 'GLOBAL').toUpperCase();
  if (text === 'DEALER' || text === 'SITE') return text;
  return 'GLOBAL';
}

/**
 * Validate and normalize an authoring document.
 * Assigns missing tmp IDs and MULTI_CHOICE option IDs; sorts by position;
 * remaps/validates link targets as draft_step_id; preserves repairable step-content issues.
 */
export function normalizeAuthoringDocument(doc: unknown): SopAuthoringDocument {
  if (doc == null || typeof doc !== 'object') {
    throw new Error('Authoring document must be an object');
  }
  const raw = doc as Record<string, unknown>;
  const schemaVersion = Number(raw.schema_version);
  if (schemaVersion !== SOP_AUTHORING_DOCUMENT_SCHEMA_VERSION) {
    throw new Error(`Unsupported authoring schema_version ${String(raw.schema_version)}`);
  }
  const sopRaw = (raw.sop ?? {}) as Record<string, unknown>;
  const name = String(sopRaw.name ?? '').trim();
  if (!name) throw new Error('SOP name is required');

  const scopeType = coerceScopeType(sopRaw.scope_type ?? sopRaw.owner_type);
  const scopeIdRaw = sopRaw.scope_id ?? sopRaw.owner_id;
  const scopeId =
    scopeType === 'GLOBAL'
      ? null
      : scopeIdRaw == null || scopeIdRaw === ''
        ? null
        : Number(scopeIdRaw);
  if (scopeType !== 'GLOBAL' && (scopeId == null || !Number.isFinite(scopeId) || scopeId <= 0)) {
    throw new Error('scope_id is required for non-global SOPs');
  }

  const sop: SopAuthoringSopMeta = {
    name,
    description: asNullableString(sopRaw.description),
    scope_type: scopeType,
    scope_id: scopeId,
    is_inheritable: parseBooleanFlag(sopRaw.is_inheritable, false),
    is_hidden_from_descendants: parseBooleanFlag(sopRaw.is_hidden_from_descendants, false),
  };

  const stepsRaw = Array.isArray(raw.steps) ? raw.steps : [];
  const draftIds = new Set<string>();
  const preliminary: Array<Record<string, unknown>> = [];

  for (let index = 0; index < stepsRaw.length; index += 1) {
    const stepRaw = (stepsRaw[index] ?? {}) as Record<string, unknown>;
    let draftStepId = String(stepRaw.draft_step_id ?? '').trim();
    const publishedRaw = stepRaw.published_step_id ?? stepRaw.id;
    const publishedStepId =
      publishedRaw != null && Number(publishedRaw) > 0 ? Number(publishedRaw) : null;

    if (!draftStepId) {
      draftStepId = publishedStepId != null ? publishedStepDraftId(publishedStepId) : newTemporaryDraftStepId();
    }
    if (publishedStepId != null && !isPublishedDraftStepId(draftStepId)) {
      draftStepId = publishedStepDraftId(publishedStepId);
    }
    if (publishedStepId == null && !isTemporaryDraftStepId(draftStepId)) {
      if (!draftStepId.startsWith('tmp:')) {
        draftStepId = newTemporaryDraftStepId();
      }
    }
    if (draftIds.has(draftStepId)) {
      throw new Error(`Duplicate draft_step_id "${draftStepId}"`);
    }
    draftIds.add(draftStepId);
    preliminary.push({ ...stepRaw, draft_step_id: draftStepId, published_step_id: publishedStepId });
  }

  const steps: SopAuthoringStep[] = preliminary.map((stepRaw, index) => {
    const draftStepId = String(stepRaw.draft_step_id);
    const publishedStepId =
      stepRaw.published_step_id != null && Number(stepRaw.published_step_id) > 0
        ? Number(stepRaw.published_step_id)
        : null;
    const position = Number(stepRaw.position ?? stepRaw.step_order ?? index + 1) || index + 1;
    const archetype = asNullableString(stepRaw.archetype);
    let config = stepRaw.config ?? null;
    if (isMultipleChoiceStep(archetype)) {
      config = normalizeMultiChoiceConfigLinks(config, draftIds);
    }

    const step: SopAuthoringStep = {
      draft_step_id: draftStepId,
      published_step_id: publishedStepId,
      position,
      title: String(stepRaw.title ?? '').trim() || 'Untitled step',
      archetype,
      variant: asNullableString(stepRaw.variant),
      prompt_text: asNullableString(stepRaw.prompt_text),
      camera_ref: asNullableString(stepRaw.camera_ref),
      url: asNullableString(stepRaw.url),
      config,
      contact_reference_type: asNullableString(stepRaw.contact_reference_type),
      contact_reference_value:
        stepRaw.contact_reference_value == null || stepRaw.contact_reference_value === ''
          ? null
          : (stepRaw.contact_reference_value as string | number),
      contact_reference_scope: asNullableString(stepRaw.contact_reference_scope),
      url_reference_type: asNullableString(stepRaw.url_reference_type),
      url_reference_value: asNullableString(stepRaw.url_reference_value),
      choice_positive_label: asNullableString(stepRaw.choice_positive_label),
      choice_negative_label: asNullableString(stepRaw.choice_negative_label),
      positive_response_format: asNullableString(stepRaw.positive_response_format),
      negative_response_format: asNullableString(stepRaw.negative_response_format),
      include_negative_in_report: parseBooleanFlag(stepRaw.include_negative_in_report, false),
      include_in_report: parseBooleanFlag(stepRaw.include_in_report, true),
      show_in_narrative_strip: parseBooleanFlag(stepRaw.show_in_narrative_strip, false),
      report_action_text: asNullableString(stepRaw.report_action_text),
      is_active: parseBooleanFlag(stepRaw.is_active, true),
      next_step: normalizeLinkTarget(stepRaw.next_step, draftIds),
      yes_next_step: normalizeLinkTarget(stepRaw.yes_next_step, draftIds),
      no_next_step: normalizeLinkTarget(stepRaw.no_next_step, draftIds),
    };
    return step;
  });

  steps.sort((a, b) => a.position - b.position || a.draft_step_id.localeCompare(b.draft_step_id));
  steps.forEach((step, index) => {
    step.position = index + 1;
  });

  return {
    schema_version: SOP_AUTHORING_DOCUMENT_SCHEMA_VERSION,
    sop,
    steps,
  };
}

/** Build a schema_version 1 document from published SOP + step rows. */
export function publishedStepsToAuthoringDocument(
  sop: PublishedSopForAuthoring,
  steps: PublishedStepForAuthoring[],
): SopAuthoringDocument {
  const publishedIds = new Set(steps.map((step) => Number(step.id)).filter((id) => id > 0));
  const scopeType = coerceScopeType(sop.scope_type ?? sop.owner_type);
  const scopeIdRaw = sop.scope_id ?? sop.owner_id;
  const document: SopAuthoringDocument = {
    schema_version: SOP_AUTHORING_DOCUMENT_SCHEMA_VERSION,
    sop: {
      name: String(sop.name || '').trim() || 'Untitled SOP',
      description: asNullableString(sop.description),
      scope_type: scopeType,
      scope_id:
        scopeType === 'GLOBAL'
          ? null
          : scopeIdRaw == null
            ? null
            : Number(scopeIdRaw),
      is_inheritable: parseBooleanFlag(sop.is_inheritable, false),
      is_hidden_from_descendants: parseBooleanFlag(sop.is_hidden_from_descendants, false),
    },
    steps: steps.map((step, index) => {
      const publishedStepId = Number(step.id);
      const draftStepId = publishedStepDraftId(publishedStepId);
      let config = step.config ?? null;
      if (isMultipleChoiceStep(step.archetype)) {
        const parsed = parseMultipleChoiceConfig(config);
        config = {
          options: parsed.options.map((option) => ({
            id: option.id,
            label: option.label,
            report_output: option.report_output,
            next_step_id: linkPublishedIdToDraftId(option.next_step_id, publishedIds) as unknown as number | null,
          })),
        };
      }
      return {
        draft_step_id: draftStepId,
        published_step_id: publishedStepId,
        position: Number(step.step_order ?? index + 1) || index + 1,
        title: String(step.title ?? '').trim() || 'Untitled step',
        archetype: asNullableString(step.archetype),
        variant: asNullableString(step.variant),
        prompt_text: asNullableString(step.prompt_text),
        camera_ref: asNullableString(step.camera_ref),
        url: asNullableString(step.url),
        config,
        contact_reference_type: asNullableString(step.contact_reference_type),
        contact_reference_value:
          step.contact_reference_value == null || step.contact_reference_value === ''
            ? null
            : step.contact_reference_value,
        contact_reference_scope: asNullableString(step.contact_reference_scope),
        url_reference_type: asNullableString(step.url_reference_type),
        url_reference_value: asNullableString(step.url_reference_value),
        choice_positive_label: asNullableString(step.choice_positive_label),
        choice_negative_label: asNullableString(step.choice_negative_label),
        positive_response_format: asNullableString(step.positive_response_format),
        negative_response_format: asNullableString(step.negative_response_format),
        include_negative_in_report: parseBooleanFlag(step.include_negative_in_report, false),
        include_in_report: parseBooleanFlag(step.include_in_report, true),
        show_in_narrative_strip: parseBooleanFlag(step.show_in_narrative_strip, false),
        report_action_text: asNullableString(step.report_action_text),
        is_active: parseBooleanFlag(step.is_active, true),
        next_step: linkPublishedIdToDraftId(step.next_step, publishedIds),
        yes_next_step: linkPublishedIdToDraftId(step.yes_next_step, publishedIds),
        no_next_step: linkPublishedIdToDraftId(step.no_next_step, publishedIds),
      };
    }),
  };
  return normalizeAuthoringDocument(document);
}

/**
 * Convert authoring steps to flow-validation shape using provisional numeric IDs
 * (published ids preserved; temps get synthetic ids) and remapped numeric links.
 */
export function authoringStepsToFlowSteps(steps: SopAuthoringStep[]): Array<{
  id: number;
  draft_step_id: string;
  step_order: number;
  title: string | null;
  archetype: string | null;
  variant: string | null;
  prompt_text: string | null;
  url: string | null;
  url_reference_value: string | null;
  config: unknown;
  contact_reference_type: string | null;
  contact_reference_value: string | number | null;
  include_in_report: boolean;
  next_step: number | null;
  yes_next_step: number | null;
  no_next_step: number | null;
  choice_positive_label: string | null;
  choice_negative_label: string | null;
}> {
  const idMap = new Map<string, number>();
  let nextSynthetic = 1_000_000_000;
  for (const step of steps) {
    if (step.published_step_id != null && step.published_step_id > 0) {
      idMap.set(step.draft_step_id, step.published_step_id);
    } else {
      idMap.set(step.draft_step_id, nextSynthetic);
      nextSynthetic += 1;
    }
  }
  const remap = (draftId: string | null): number | null => {
    if (!draftId) return null;
    return idMap.get(draftId) ?? null;
  };
  return steps.map((step) => {
    let config = step.config;
    if (isMultipleChoiceStep(step.archetype)) {
      const rawOptions =
        step.config != null && typeof step.config === 'object' && Array.isArray((step.config as any).options)
          ? ((step.config as any).options as Array<Record<string, unknown>>)
          : [];
      config = {
        options: rawOptions.map((option, index) => {
          const nextRaw = option.next_step_id ?? option.next_step ?? null;
          const draftNext =
            nextRaw == null || nextRaw === ''
              ? null
              : String(nextRaw).startsWith('step:') || String(nextRaw).startsWith('tmp:')
                ? String(nextRaw)
                : Number.isFinite(Number(nextRaw)) && Number(nextRaw) > 0
                  ? publishedStepDraftId(Number(nextRaw))
                  : null;
          return {
            id: String(option.id ?? `option_${index + 1}`),
            label: String(option.label ?? ''),
            report_output: String(option.report_output ?? option.reportText ?? ''),
            next_step_id: draftNext ? remap(draftNext) : null,
          };
        }),
      };
    }
    return {
      id: idMap.get(step.draft_step_id)!,
      draft_step_id: step.draft_step_id,
      step_order: step.position,
      title: step.title,
      archetype: step.archetype,
      variant: step.variant,
      prompt_text: step.prompt_text,
      url: step.url,
      url_reference_value: step.url_reference_value,
      config,
      contact_reference_type: step.contact_reference_type,
      contact_reference_value: step.contact_reference_value,
      include_in_report: step.include_in_report,
      next_step: remap(step.next_step),
      yes_next_step: remap(step.yes_next_step),
      no_next_step: remap(step.no_next_step),
      choice_positive_label: step.choice_positive_label,
      choice_negative_label: step.choice_negative_label,
    };
  });
}
